import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import api from "../../../../services/api";
import { USEQUERY_DISABLE_REFETCH_OPTIONS } from "../../../network/consts";
import {
  buildNormalizedSet,
  normalizeMutexValue,
  normalizeNetworkValue,
  normalizeUrlValue
} from "./normalize";

const TICORE_FIELDS = [
  "malware.servers",
  "malware.run_mutex",
  "malware.log_mutex",
  "info.package"
];

const SERVER_PROPERTY_PREFIXES = ["botserver", "botftpserver", "boturl"];
const MUTEX_PROPERTY_PREFIXES = ["botrunmutex", "botlogmutex"];

type PackageProperty = { name?: string; value?: string };

type MalwareServer = {
  domain_name?: string;
  domain_ip?: string;
  remote_path?: string;
};

type TiCoreConfigResponse = {
  malware?: {
    servers?: MalwareServer[];
    run_mutex?: string;
    log_mutex?: string;
  };
  info?: {
    package?: { properties?: PackageProperty[] };
  };
};

const matchesPrefix = (name: string, prefixes: string[]) =>
  prefixes.some(prefix => name.startsWith(prefix));

const pickProperties = (properties: PackageProperty[], prefixes: string[]) =>
  properties
    .filter(property =>
      matchesPrefix((property.name || "").toLowerCase(), prefixes)
    )
    .map(property => property.value);

export const extractSpectraCoreConfig = (
  response: TiCoreConfigResponse | null
) => {
  const servers = response?.malware?.servers || [];
  const properties = response?.info?.package?.properties || [];

  const serverValues = servers.flatMap(server => [
    server.domain_name,
    server.domain_ip
  ]);

  const serverUrls = servers.flatMap(server =>
    server.domain_name && server.remote_path
      ? [`${server.domain_name}${server.remote_path}`]
      : []
  );

  const mutexValues = [
    response?.malware?.run_mutex,
    response?.malware?.log_mutex,
    ...pickProperties(properties, MUTEX_PROPERTY_PREFIXES)
  ];

  return {
    c2: buildNormalizedSet(
      [
        ...serverValues,
        ...pickProperties(properties, SERVER_PROPERTY_PREFIXES)
      ],
      normalizeNetworkValue
    ),
    c2Urls: buildNormalizedSet(serverUrls, normalizeUrlValue),
    mutexes: buildNormalizedSet(mutexValues, normalizeMutexValue)
  };
};

const useSpectraCoreMalwareConfig = (hash?: string) => {
  const { data } = useQuery({
    queryKey: ["spectraCoreMalwareConfig", hash],
    queryFn: () => api.tcAnalysisMetadata(hash, TICORE_FIELDS),
    select: response => response?.data ?? null,
    enabled: Boolean(hash),
    ...USEQUERY_DISABLE_REFETCH_OPTIONS
  });

  return useMemo(() => extractSpectraCoreConfig(data ?? null), [data]);
};

export default useSpectraCoreMalwareConfig;
